/**
 * A job's contract total — what the customer signed plus the net of each
 * change order added to the job.
 *
 * ONE resolver for the Job Details card and the Jobs list, so the two always
 * agree. All values are integer cents.
 *
 * Order (IDI-560, Adrian 10-22; supersedes IDI-478's order):
 *   1. totalAmount                         (convert writes the signed total;
 *                                           Add to Job adds each CO's net;
 *                                           migrated jobs)
 *   2. lockedTotalCents                    (the signed snapshot — jobs
 *                                           converted before IDI-560)
 *   3. subtotal + taxAmount, both present  (no signed total at all)
 *   4. none → null. Rendered as "—", never a guessed $0.
 *
 * subtotal + taxAmount ranks below the signed total because it leaves out
 * the signed discount: a discounted job read high by the discount.
 *
 * An explicit stored 0 is a value, not "none".
 *
 * @param {object} job
 * @returns {{ totalCents: number, subtotalCents: number|null, discountCents: number|null, taxCents: number|null } | null}
 */
export function getJobContractTotal(job) {
  if (!job) return null;
  const subtotalCents = readCents(job.subtotal);
  const taxCents = readCents(job.taxAmount ?? job.tax_amount);
  const storedDiscount = readCents(job.discountAmount ?? job.discount_amount);
  const total = readCents(job.totalAmount ?? job.total_amount);
  const locked = readCents(job.lockedTotalCents ?? job.locked_total_cents);

  // IDI-565 (D3): a signed job converted before IDI-560 stored no discount.
  // Derive it (subtotal + tax − total, if above $0) so the hover adds up.
  // Jobs without a signed total (migrated, IDI-47) keep the record's values.
  let discountCents = storedDiscount;
  const resolved = total ?? locked;
  if (storedDiscount == null && locked != null && subtotalCents != null && resolved != null) {
    const derived = subtotalCents + (taxCents ?? 0) - resolved;
    discountCents = derived > 0 ? derived : null;
  }
  const parts = { subtotalCents, discountCents, taxCents };

  if (total != null) return { totalCents: total, ...parts };

  if (locked != null) return { totalCents: locked, ...parts };

  if (subtotalCents != null && taxCents != null) {
    return { totalCents: subtotalCents + taxCents, ...parts };
  }

  return null;
}

function readCents(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : null;
}
